import { Dispatch, useState } from 'react';
import { Modal, Button, Alert, Spinner} from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import { IAgent } from '../../models/agent';
import { unPublishAgent } from '../../api/agent';
import { FaPowerOff, FaExclamationTriangle } from 'react-icons/fa';

type Props = {
    agent: IAgent;
    cancel: Dispatch<React.SetStateAction<boolean>>
    setError: Dispatch<React.SetStateAction<string>>
}

const ConfirmDisableAgent = ({agent, cancel} : Props) => {
    const [error, setError] = useState<string | null>(null);
    const handleClose = () => cancel(false);

    const queryClient = useQueryClient();
    const { mutateAsync: unPublishAgentFn, isLoading} = useMutation(
        unPublishAgent,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Agents', agent.organizationId]);
                handleClose();
            },
            onError: (error) => {
                setError("Unexpected error. Please try again or contact admin.");
                handleClose();
            },
        }
    );

    const handleUnPublish = async () => {
        if(!agent) {
            setError("Agent not found.");
            handleClose();
            return;
        }
        unPublishAgentFn({orgId: agent.organizationId, id: agent.id});
    }

    return (
        <Modal 
            show={!!agent} 
            onHide={handleClose} 
            centered
            contentClassName="bg-dark text-light"
        >
            <Modal.Header 
                closeButton 
                className="border-0 pb-0"
                style={{ backgroundColor: '#2d2d2d', borderBottom: '1px solid #444' }}
            >
                <Modal.Title className="d-flex align-items-center text-warning">
                    <FaExclamationTriangle className="me-2" />
                    Disable Agent
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body className="pt-2" style={{ backgroundColor: '#2d2d2d' }}>
                <div className="d-flex align-items-start">
                    <div className="bg-warning bg-opacity-20 rounded p-3 me-3 mt-1">
                        <FaPowerOff className="text-dark" size={24} />
                    </div>
                    <div className="flex-grow-1">
                        <h6 className="mb-2 text-light">Are you sure you want to disable this agent?</h6>
                        <p style={{ color: '#ccc' }} className="mb-2">
                            This will unpublish the agent <strong className="text-light">"{agent.name}"</strong> and stop all active deployments.
                        </p>
                        <div 
                            className="border border-warning rounded p-3 mt-3"
                            style={{ backgroundColor: '#2d2a1a', borderColor: '#ffc107' }}
                        >
                            <p className="text-warning mb-0 small fw-semibold">
                                ⚠️ All deployments will stop working immediately. You can re-enable the agent later.
                            </p>
                        </div>
                    </div>
                </div>

                {error && (
                    <Alert 
                        variant="danger" 
                        className="mt-3 mb-0"
                        style={{ backgroundColor: '#2c1618', borderColor: '#dc3545', color: '#f5c6cb' }}
                    >
                        <FaExclamationTriangle className="me-2" />
                        {error}
                    </Alert>
                )}
            </Modal.Body>
            
            <Modal.Footer 
                className="border-0 pt-0"
                style={{ backgroundColor: '#2d2d2d' }}
            >
                <Button 
                    variant="outline-secondary" 
                    onClick={handleClose} 
                    disabled={isLoading}
                    style={{ borderColor: '#555', color: '#ccc' }}
                >
                    Cancel
                </Button>
                <Button variant="warning" onClick={handleUnPublish} disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Disabling...
                        </>
                    ) : (
                        <>
                            <FaPowerOff className="me-2" />
                            Disable Agent
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ConfirmDisableAgent;